import { CONSENT_FIELD_TYPE } from "@/pages/app/provider/consent-forms/ccm-consent/ccm-consent-constant";
import {
  ConsentFieldConfig,
  ConsentOption,
} from "@/pages/app/provider/consent-forms/ccm-consent/ccm-consent-types";
import {
  PatientConsentFormValues,
  PatientReleaseItem,
  TreatmentConsentItem,
  YesNoAnswer,
} from "./patient-consent-types";

export const PATIENT_CONSENT_FORM_ID = "patient-consent-form";

export const YES_NO = { YES: "YES", NO: "NO" } as const;

export const PATIENT_RELEASE_ITEM = {
  HISTORY_AND_PHYSICALS: "HISTORY_AND_PHYSICALS",
  CURRENT_MEDICAL_FINDINGS: "CURRENT_MEDICAL_FINDINGS",
  CHART_NOTES: "CHART_NOTES",
  MEDICATION_LIST: "MEDICATION_LIST",
  LAB_AND_IMAGING_REPORTS: "LAB_AND_IMAGING_REPORTS",
  TREATMENT_PLAN: "TREATMENT_PLAN",
} as const;

export const PATIENT_DEFAULT_EXPIRY_MONTHS = 6;

export const PATIENT_CONSENT_TEXT = {
  TITLE: "PATIENT CONSENT FORM",
  RELEASE_AUTHORIZATION:
    "AUTHORIZATION TO RELEASE PATIENT HEALTH INFORMATION TO",
  TREATMENT_CONSENT:
    "Consent for Treatment and Acknowledgement Receipt of HIPAA Notice",
  CONTACT_NUMBER: "Contact Number",
  AUTHORIZATION_EXPIRES:
    "Authorization expires (unless otherwise noted, this authorization will remain in effect six months from the date signed):",
  NEVER: "Never",
  DATE: "Date",
  RELEASE_FOLLOWING: "Release the following information:",
  YES: "Yes",
  NO: "No",
  PATIENT_SIGNATURE: "Patient Signature",
  RESPONSIBLE_SIGNATURE: "Responsible Person / Guardian Signature",
  EMAIL_ERROR: "Enter a valid email address",
} as const;

export const PATIENT_FIELDS: ConsentFieldConfig[] = [
  { name: "patient.patientName", label: "Patient Name", required: true, sm: 8 },
  {
    name: "patient.dateOfService",
    label: "Date of Service",
    type: CONSENT_FIELD_TYPE.DATE,
    allowFutureDate: true,
    sm: 4,
  },
  {
    name: "patient.dateOfBirth",
    label: "Date of Birth",
    type: CONSENT_FIELD_TYPE.DATE,
    required: true,
    sm: 8,
  },
  { name: "patient.gender", label: "Gender", sm: 4 },
  { name: "patient.address", label: "Address", sm: 12 },
  { name: "patient.emergencyContact", label: "Emergency Contact", sm: 5 },
  { name: "patient.relationship", label: "Relationship", sm: 4 },
  {
    name: "patient.phone",
    label: "Phone",
    type: CONSENT_FIELD_TYPE.PHONE,
    sm: 3,
  },
];

export const RELEASE_CONTACT_FIELDS: ConsentFieldConfig[] = [
  { name: "releaseAuthorization.requestName", label: "Request Name", sm: 4 },
  { name: "releaseAuthorization.address", label: "Address", sm: 8 },
  {
    name: "releaseAuthorization.phone",
    label: "Phone",
    type: CONSENT_FIELD_TYPE.PHONE,
    sm: 3,
  },
  {
    name: "releaseAuthorization.fax",
    label: "Fax",
    type: CONSENT_FIELD_TYPE.PHONE,
    sm: 3,
  },
  { name: "releaseAuthorization.email", label: "Email", sm: 6 },
];

export const RELEASE_EXPIRY_DATE_FIELD: ConsentFieldConfig = {
  name: "releaseAuthorization.expiryDate",
  label: "Date",
  type: CONSENT_FIELD_TYPE.DATE,
  allowFutureDate: true,
};

export const SIGNATORY_FIELDS: ConsentFieldConfig[] = [
  {
    name: "signature.date",
    label: "Date",
    type: CONSENT_FIELD_TYPE.DATE,
    sm: 6,
  },
  {
    name: "signature.unableToSignReason",
    label: "If Patient Unable to Sign, Due to",
    sm: 6,
  },
  {
    name: "signature.signatoryName",
    label: "Name of Signatory (If different from patient)",
    sm: 6,
  },
  {
    name: "signature.relationshipToPatient",
    label: "Relationship to Patient",
    sm: 6,
  },
];

export const PATIENT_RELEASE_ITEM_OPTIONS: ConsentOption<PatientReleaseItem>[] =
  [
    {
      value: PATIENT_RELEASE_ITEM.HISTORY_AND_PHYSICALS,
      label: "History & Physicals",
    },
    {
      value: PATIENT_RELEASE_ITEM.CURRENT_MEDICAL_FINDINGS,
      label: "Current Medical Findings",
    },
    { value: PATIENT_RELEASE_ITEM.CHART_NOTES, label: "Chart Notes" },
    {
      value: PATIENT_RELEASE_ITEM.MEDICATION_LIST,
      label: "Previous & Present Medication List",
    },
    {
      value: PATIENT_RELEASE_ITEM.LAB_AND_IMAGING_REPORTS,
      label: "Lab & Imaging Reports",
    },
    {
      value: PATIENT_RELEASE_ITEM.TREATMENT_PLAN,
      label: "Treatment Plan, if determined",
    },
  ];

export const YES_NO_OPTIONS: ConsentOption<YesNoAnswer>[] = [
  { value: YES_NO.YES, label: PATIENT_CONSENT_TEXT.YES },
  { value: YES_NO.NO, label: PATIENT_CONSENT_TEXT.NO },
];

export const PREFILLED_PATIENT_FIELD_NAMES: readonly string[] = [
  "patient.patientName",
  "patient.dateOfBirth",
  "patient.gender",
  "patient.address",
  "patient.emergencyContact",
  "patient.relationship",
];

export const TREATMENT_CONSENT_ITEMS: TreatmentConsentItem[] = [
  {
    id: "home-health-assessment",
    text: "By this document, I hereby consent to have Premier MD personnel perform a home health assessment visit to ensure all requirements by CMS (Centers for Medicare and Medicaid Services) are met.",
  },
  {
    id: "hipaa-receipt",
    text: "I acknowledge the receipt of notice of privacy (HIPAA Form) and was given the opportunity to ask questions and voice concerns. I give permission to Premier MD to use and disclose protective health information about me to carry out treatment, payment, health care operations, receive and release information pertaining to my care.",
  },
  {
    id: "photograph-consent",
    text: "I authorize a representative of Premier MD to photograph or videotape appropriate body parts for necessary documentation only.",
    answerField: "treatmentConsent.photographConsent",
  },
  {
    id: "records-release",
    text: "I certify that all information is correct and consent to the release of all records for payments of authorized benefits from Medicare, Medicaid, or any other responsible party on my behalf to Premier MD.",
  },
  {
    id: "self-determination",
    text: "I understand that the federal patient self-determination act of 1990 requires that I be made aware of my right to make healthcare decisions for myself. I understand that I may express my wishes in a document called Advance Directives so that my wishes may be known when I am unable to speak for myself.",
  },
  {
    id: "living-will",
    text: "I have a living will declaration.",
    answerField: "treatmentConsent.hasLivingWill",
    note: "If yes, please provide a copy of your will.",
  },
  {
    id: "power-of-attorney",
    text: "I have made a durable power of attorney for health care.",
    answerField: "treatmentConsent.hasPowerOfAttorney",
  },
];

export const PATIENT_CONSENT_INITIAL_VALUES: PatientConsentFormValues = {
  patient: {
    patientName: "",
    dateOfService: null,
    dateOfBirth: null,
    gender: "",
    address: "",
    emergencyContact: "",
    relationship: "",
    phone: "",
  },
  releaseAuthorization: {
    requestName: "",
    address: "",
    phone: "",
    fax: "",
    email: "",
    expiry: "",
    expiryDate: null,
    releaseItems: [],
  },
  treatmentConsent: {
    photographConsent: "",
    hasLivingWill: "",
    hasPowerOfAttorney: "",
  },
  signature: {
    date: null,
    unableToSignReason: "",
    signatoryName: "",
    relationshipToPatient: "",
  },
  acknowledgement: { relationshipToPatient: "", witnessName: "", date: null },
};
